import { type QiraMessage, type QiraMessageContentChunk } from 'api3';
import { Component, computed, effect, inject, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '@app/services/message.service';
import { ScrollService } from '@app/services/scroll.service';
import { TabService } from '@app/services/tab.service';
import { ChatTab, DisplayVehicle } from '@app/services/types';
import { ChatIconComponent } from '@app/components/chat-icon/chat-icon.component';
import { MessageContentIntentSuggestionsComponent } from '@app/chat/components/message-content-intent-suggestions.html/message-content-intent-suggestions.component';
import { MessageContentTextComponent } from '@app/chat/components/message-content-text/message-content-text.component';
import { MessageFeedbackLinksComponent } from '@app/chat/components/message-feedback-links/message-feedback-links.component';
import { MessageVoiceToggleComponent } from '@app/chat/components/message-voice-toggle/message-voice-toggle.component';
import { SmartSearchModalButtonComponent } from '@app/chat/components/smart-search/smart-search-modal-button/smart-search-modal-button.component';
import { SmartSearchComponent } from '../smart-search/smart-search.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';

@Component({
  selector: 'app-chat-messages',
  templateUrl: './chat-messages.component.html',
  standalone: true,
  imports: [
    TranslocoPipe,
    MarkdownModule,
    MessageContentTextComponent,
    MessageContentIntentSuggestionsComponent,
    MessageFeedbackLinksComponent,
    MessageVoiceToggleComponent,
    ChatIconComponent,
    SmartSearchModalButtonComponent,
    SmartSearchComponent,
  ],
})
export class ChatMessagesComponent {
  currentThread = input<ChatTab>();
  activeTab = input<ChatTab>();
  vehicle = input<DisplayVehicle>();
  currentMessages = input<QiraMessage[]>();
  lastMessageCount = 0;

  tabService = inject(TabService);
  scrollService = inject(ScrollService);
  route = inject(ActivatedRoute);
  messageService = inject(MessageService);

  infoMessage = computed(() => this._getInfoMessage(this.currentMessages() || []));

  observeMessages = effect(() => {
    const messagesCount = this.currentMessages()?.length;
    if (messagesCount && this.activeTab()?._id === this.currentThread()?._id) {
      setTimeout(this._scrollToLastQuestion.bind(this), 300);

      if (this.lastMessageCount === 0) {
        setTimeout(this._scrollToLastQuestion.bind(this), 1000);
      }

      this.lastMessageCount = messagesCount;
    }
  });

  isLastMessage(message: QiraMessage) {
    const messagesLength = this.currentMessages()?.length || 0;
    if (!messagesLength || !message) return false;
    return this.currentMessages()?.indexOf(message) === messagesLength - 1;
  }

  isSmartSearchMessage(content: QiraMessageContentChunk | undefined): boolean {
    const type = this.messageService.getMessageType(content);
    return type === 'document' || type === 'widget';
  }

  getNoOfSmartSearchResults(contents: QiraMessageContentChunk[] | undefined): number {
    if (!contents) return 0;
    return contents.filter((contentChunk) => this.isSmartSearchMessage(contentChunk)).length || 0;
  }

  containsSmartSearchMessage(contents: QiraMessageContentChunk[] | undefined): boolean {
    if (!contents) return false;
    return contents.some((content) => this.isSmartSearchMessage(content));
  }

  hasRecallSource(content: QiraMessageContentChunk[] | undefined): boolean {
    if (!content) return false;

    return content.some((contentChunk) => {
      const hasRecallSource = contentChunk.sources?.some((source) => {
        return source.subject === 'Recalls';
      });
      const hasRecallDocument = contentChunk.document?.subject === 'Recalls';
      return hasRecallDocument || hasRecallSource;
    });
  }

  private _getInfoMessage(messages: QiraMessage[]) {
    // run backwards
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];

      // no info found within the first question cycle
      if (this.tabService.isChatQuestion(message)) {
        break;
      }

      if (this.tabService.isInfoMessage(message)) {
        return message;
      }

      // message is already answered (no info display)
      if (this.tabService.isChatAnswer(message)) {
        break;
      }
    }
    return undefined;
  }

  private _scrollToLastQuestion() {
    const lastMessage = [...(this.currentMessages() || [])].reverse().find((message) => message.sender === 'you');

    this.scrollService.scrollToMessage(lastMessage?.id, this.activeTab());
  }
}
